import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";

const NavContainer = styled.nav`
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 0;
  position: sticky;
  top: 0;
  z-index: 100;
  transition: all 0.3s ease;
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
  padding: 0 20px;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #333;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.5px;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const LogoImage = styled.img`
  height: 40px;
  margin-right: 12px;
  border-radius: 8px;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: #333;
  margin-left: 5px;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  position: relative;
  transition: all 0.2s ease;
  background-color: ${(props) => (props.active ? "#f0f7e6" : "transparent")};

  &:hover {
    background-color: ${(props) => (props.active ? "#e8f5d9" : "#f5f5f5")};
    transform: translateY(-2px);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 5px;
    left: 50%;
    width: ${(props) => (props.active ? "20px" : "0")};
    height: 3px;
    background-color: #6ed216;
    transform: translateX(-50%);
    transition: width 0.3s ease;
    border-radius: 3px;
  }

  &:hover::after {
    width: ${(props) => (props.active ? "30px" : "10px")};
  }
`;

const NavButton = styled.button`
  background-color: ${(props) => (props.primary ? "#6ed216" : "transparent")};
  color: ${(props) => (props.primary ? "white" : "#333")};
  border: ${(props) => (props.primary ? "none" : "1px solid #ddd")};
  border-radius: 8px;
  padding: 10px 18px;
  margin-left: 15px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  box-shadow: ${(props) =>
    props.primary ? "0 4px 12px rgba(110, 210, 22, 0.2)" : "none"};

  &:hover {
    background-color: ${(props) => (props.primary ? "#5cb70f" : "#f5f5f5")};
    transform: translateY(-2px);
    box-shadow: ${(props) =>
      props.primary
        ? "0 6px 16px rgba(110, 210, 22, 0.3)"
        : "0 4px 8px rgba(0, 0, 0, 0.05)"};
  }

  svg {
    margin-right: 8px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  background-color: #f8f9fa;
  padding: 6px 12px;
  border-radius: 30px;
  margin-right: 15px;
  transition: all 0.3s;

  &:hover {
    background-color: #f0f0f0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #6ed216;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 10px;
  font-size: 14px;
`;

const UserName = styled.span`
  font-weight: 500;
  color: #333;
`;

const UserRole = styled.span`
  background-color: ${(props) => (props.isAdmin ? "#e8f0fe" : "#e6f4ea")};
  color: ${(props) => (props.isAdmin ? "#1a73e8" : "#137333")};
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 8px;
  box-shadow: 0 2px 4px
    ${(props) =>
      props.isAdmin ? "rgba(26, 115, 232, 0.1)" : "rgba(19, 115, 51, 0.1)"};
`;

// Icônes SVG pour les boutons
const LogoutIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 17L21 12L16 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 12H9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UsersIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HomeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 22V12H15V22"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function Navbar({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  // Effet pour détecter le défilement
  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = () => {
    onLogout();
    navigate("/login");
  };

  // Obtenir les initiales de l'utilisateur pour l'avatar
  const getUserInitials = () => {
    if (!user || !user.username) return "U";
    return user.username.charAt(0).toUpperCase();
  };

  return (
    <NavContainer
      style={{
        boxShadow: isScrolled ? "0 4px 12px rgba(0, 0, 0, 0.08)" : "none",
      }}
    >
      <NavContent>
        <Logo to="/">
          <LogoImage src="/logo.png" alt="Logo" />
          Xandr Reports
        </Logo>

        <NavLinks>
          {user ? (
            <>
              <UserInfo>
                <UserAvatar>{getUserInitials()}</UserAvatar>
                <UserName>{user.username}</UserName>
                <UserRole>
                  {user.role === "admin" ? "Admin" : "Commercial"}
                </UserRole>
              </UserInfo>

              <NavLink to="/" active={location.pathname === "/"}>
                <HomeIcon /> Accueil
              </NavLink>

              {user.role === "admin" && (
                <NavLink
                  to="/admin/users"
                  active={location.pathname === "/admin/users"}
                >
                  <UsersIcon /> Utilisateurs
                </NavLink>
              )}

              <NavButton onClick={handleLogout}>
                <LogoutIcon /> Déconnexion
              </NavButton>
            </>
          ) : (
            <>
              <NavLink to="/login" active={location.pathname === "/login"}>
                Connexion
              </NavLink>
              <NavButton $primary onClick={() => navigate("/register")}>
                Inscription
              </NavButton>
            </>
          )}
        </NavLinks>
      </NavContent>
    </NavContainer>
  );
}

export default Navbar;
